import { isDisplayMediaTrack } from './is-display-media-track'
import { VIRTUAL_AUDIO_DEVICE_IDS } from './virtual-audio-device-ids'

type DeviceLike = {
  deviceId: string
  groupId?: string
}

/**
 * Returns true when the track is live and already bound to the given deviceId.
 * Used to skip redundant getUserMedia when replacing mic/cam on active sessions.
 *
 * Chrome: getUserMedia({ deviceId: { exact: 'default' } }) yields a track whose
 * getSettings().deviceId is the real hardware id — match via groupId when possible.
 *
 * Display/screen tracks must never count as an already-selected webcam.
 */
export const isLiveMediaTrackFromDevice = (
  track: MediaStreamTrack | undefined,
  deviceId: string | undefined,
  knownDevices?: Iterable<DeviceLike>,
): boolean => {
  if (!track || track.readyState !== 'live') {
    return false
  }

  if (isDisplayMediaTrack(track)) {
    return false
  }

  if (!deviceId) {
    return true
  }

  const settingsDeviceId = track.getSettings?.()?.deviceId
  if (!settingsDeviceId) {
    return false
  }

  if (settingsDeviceId === deviceId) {
    return true
  }

  if (!VIRTUAL_AUDIO_DEVICE_IDS.has(deviceId) && !VIRTUAL_AUDIO_DEVICE_IDS.has(settingsDeviceId)) {
    return false
  }

  if (!knownDevices) {
    return false
  }

  const devices = Array.from(knownDevices)
  const target = devices.find((device) => device.deviceId === deviceId)
  const actual = devices.find((device) => device.deviceId === settingsDeviceId)

  if (target?.groupId && actual?.groupId) {
    return target.groupId === actual.groupId
  }

  return false
}
